import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { CommonTableColumnsEnum } from '../../components/Table/types'
import { SortDirectionCapital } from '../../types'
import { getInitialPaginationData, getInitialTableColumns } from '../../utils'

import {
  CashFlowBalanceRequestType,
  CashFlowRequestType,
  CashFlowState,
  CashFlowEntry,
} from './types'

export const fetchCashFlows = createAsyncThunk(
  'cashFlow/fetchCashFlows',
  async (pagination: CashFlowRequestType) => {
    try {
      const { CashFlowService } = await import('./CashFlowService')
      const { data } = await CashFlowService.getCashFlows(pagination)
      return data
    } catch (error) {
      throw Error(error.message)
    }
  },
)

export const fetchCashFlowBalance = createAsyncThunk(
  'cashFlow/fetchCashFlowBalance',
  async (request: CashFlowBalanceRequestType) => {
    try {
      const { CashFlowService } = await import('./CashFlowService')
      const { data } = await CashFlowService.getCashFlowBalance(request)
      return data
    } catch (error) {
      throw Error(error.message)
    }
  },
)

const initialState: CashFlowState = {
  rows: [],
  balance: 0,
  balanceLoading: false,
  pagination: {
    ...getInitialPaginationData<CashFlowEntry>(
      getInitialTableColumns().cashFlows as (keyof CashFlowEntry)[],
    ),
    order: SortDirectionCapital.desc,
    orderBy: CommonTableColumnsEnum.date,
  },
}

const cashFlowSlice = createSlice({
  name: 'cashFlow',
  initialState,
  reducers: {
    updateCashFlowColumnOrder: (state, action) => {
      state.pagination.columns = action.payload
    },
    updateCashFlowPageSize: (state, action) => {
      state.pagination.perPage = action.payload
    },
    updateCashFlowPage: (state, action) => {
      state.pagination.page = action.payload
    },
    updateCashFlowOrderBy: (state, action) => {
      state.pagination.page = 1
      state.pagination.order = SortDirectionCapital.asc
      state.pagination.orderBy = action.payload
    },
    updateCashFlowSortOrder: (state, action) => {
      state.pagination.page = 1
      state.pagination.order = action.payload
    },
    resetCashFlowView: (state) => {
      state.pagination.page = 1
      state.pagination.order = SortDirectionCapital.desc
      state.pagination.orderBy = CommonTableColumnsEnum.date
    },
    clearCashFlow: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCashFlowBalance.pending, (state) => {
        state.balanceLoading = true
      })
      .addCase(fetchCashFlows.fulfilled, (state, action) => {
        state.rows = action.payload.data
        state.pagination = {
          ...state.pagination,
          ...action.payload.meta,
        }
      })
      .addCase(fetchCashFlowBalance.fulfilled, (state, action) => {
        state.balance = action.payload.balance
        state.balanceLoading = false
      })
      .addCase(fetchCashFlowBalance.rejected, (state) => {
        state.balanceLoading = false
      })
  },
})

export const {
  updateCashFlowColumnOrder,
  updateCashFlowOrderBy,
  updateCashFlowPage,
  updateCashFlowPageSize,
  updateCashFlowSortOrder,
  resetCashFlowView,
  clearCashFlow,
} = cashFlowSlice.actions

export default cashFlowSlice.reducer
